import React, { useCallback } from 'react'

import { Box, Color, DisplayPosition, withCitrusTheme, ZIndex } from '@gousto-internal/citrus-react'
import { useDispatch, useSelector } from 'react-redux'

import { Basket } from '@features/basket'

import * as trackingKeys from 'actions/trackingKeys'
import { NumPersonsToBoxDescriptors } from 'routes/BoxPrices/boxPricesModels'
import { getNumPersonsToBoxDescriptors } from 'routes/BoxPrices/boxPricesSelectors'
import { useGetYourBoxData } from 'routes/Checkout/Components/BoxDetailsHighlight/YourBox'
import { getBasketRecipesIDs } from 'routes/Checkout/Components/CheckoutBoxTangibility/selectors'
import { checkoutBasket } from 'routes/Menu/actions/menuCheckoutClick'
import { showDetailRecipe } from 'routes/Menu/actions/menuRecipeDetails'
import { useBasket } from 'routes/Menu/domains/basket'
import { usePricing } from 'routes/Menu/domains/pricing'
import { getBrowserType } from 'selectors/browser'

import { useCheckoutButtonIsDisabled } from '../../hooks/useCheckoutButtonIsDisabled'
import { Recipe } from './types'

const Container = withCitrusTheme(Box, () => ({
  margin: '0 auto',
}))

export const BasketContainer = () => {
  const isDisabled = useCheckoutButtonIsDisabled()
  const { maxRecipesNum, numPortions, menuRecipesStore } = useGetYourBoxData()
  const basketRecipesIds = useSelector(getBasketRecipesIDs)

  const selectedRecipes = basketRecipesIds.map((recipeId: number) => {
    const recipe = menuRecipesStore.get(recipeId.toString())?.toJS() as Recipe
    const defaultImageCrops = recipe?.media?.images?.[0].urls || []
    const moodImageCrops = recipe?.media?.images.find((image) => image.type === 'mood-image')?.urls

    return {
      moodImage: { crops: moodImageCrops || defaultImageCrops },
      name: recipe?.title,
      uuid: recipe?.uuid,
    }
  })

  const boxPrices = useSelector(getNumPersonsToBoxDescriptors) as NumPersonsToBoxDescriptors
  const boxPricesForNumPortions = boxPrices?.[numPortions] || []
  const pricesPerServing = [
    '', // Placeholder for the first recipe
    ...boxPricesForNumPortions.map((prices) => prices.price_per_portion),
  ]

  const dispatch = useDispatch()
  const { pricing } = usePricing()
  const view = useSelector(getBrowserType)
  const { removeRecipe, recipeCount } = useBasket()
  const section = trackingKeys.menu

  const handleCheckout = useCallback(
    (e) => {
      e.stopPropagation()
      dispatch(checkoutBasket({ section, view, pricing, removeRecipe }))
    },
    [dispatch, section, view, pricing, removeRecipe],
  )

  const handleRecipeClick = useCallback(
    (recipeUuid) => dispatch(showDetailRecipe(recipeUuid)),
    [dispatch],
  )

  // If there are no recipes in the basket, don't show the basket
  if (recipeCount <= 0) {
    return null
  }

  return (
    <Box
      bg={Color.ColdGrey_50}
      bottom="0"
      left="0"
      position={DisplayPosition.Fixed}
      width="100%"
      zIndex={ZIndex.Sticky}
    >
      <Container maxWidth="1600px">
        <Basket
          isCheckoutDisabled={isDisabled}
          isLoading={menuRecipesStore.size <= 0}
          maxNumberRecipes={maxRecipesNum}
          onCheckout={handleCheckout}
          onRecipeClick={(recipeUuid) => handleRecipeClick(recipeUuid)}
          pricesPerServing={pricesPerServing}
          selectedRecipes={selectedRecipes}
          totalPrice={pricing?.total}
        />
      </Container>
    </Box>
  )
}
